// apps/web/src/lib/constraints/useConstraintEvaluation.ts
//
// Phase 9 Plan 10 Wave-4 — React hook that wraps the pure `evaluate()`
// dispatcher (Plan 09-06) with a 100ms debounce + referentially stable
// EvaluationResult (UI-SPEC §"Performance Contract" #3-4).
//
// CSTR-01 / CSTR-03 / CSTR-04 / CSTR-05 surfacing path: the Constraints
// section subscribes to this hook each render; cycles + winning/superseded
// sets + visibility-by-widget are all consumed inline (CycleErrorBanner +
// ConflictBadge), and the SpreadComposer (Plan 09-11) consumes the same
// shape for option-disabled tooltips.
//
// AUTHR-06 keystroke budget: a 100ms debounce is the load-bearing pitfall-6
// mitigation. Without it, every keystroke into the explanation Textarea (or
// any other form path) would trigger a full evaluate() pass against the
// entire constraint set, dragging the keystroke budget back over the Phase 8
// 13.73ms p99 baseline. With the debounce, evaluate() runs at most every
// 100ms regardless of typing speed.
//
// Reference shape — RESEARCH §"useConstraintEvaluation" lines 2206-2233.

import { useEffect, useRef, useState } from "react";
import {
  type Constraint,
  type EvalContext,
  evaluate,
  type EvaluationResult,
} from "@the-recipe-spread/shared";

const DEBOUNCE_MS = 100;

/**
 * Debounced reactive wrapper over the pure `evaluate(constraints, context)`
 * dispatcher. Returns a referentially stable EvaluationResult — re-evaluates
 * 100ms after the last input change, never per-keystroke.
 *
 * First render evaluates synchronously (useState initializer) so consumers
 * never see an "empty" result on initial mount.
 *
 * inputsRef carries the latest inputs into the deferred setTimeout closure —
 * if the user types again before the timer fires, we read the most recent
 * snapshot when it eventually does fire (not the snapshot captured at the
 * time the timer was scheduled).
 */
export function useConstraintEvaluation(
  constraints: ReadonlyArray<Constraint>,
  context: EvalContext,
): EvaluationResult {
  const [result, setResult] = useState<EvaluationResult>(() => evaluate(constraints, context));
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputsRef = useRef<{ constraints: ReadonlyArray<Constraint>; context: EvalContext }>({
    constraints,
    context,
  });

  useEffect(() => {
    inputsRef.current = { constraints, context };
    if (timerRef.current !== null) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      const { constraints: c, context: ctx } = inputsRef.current;
      setResult(evaluate(c, ctx));
    }, DEBOUNCE_MS);
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [constraints, context]);

  return result;
}
